import React, { useCallback, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import toastService from '@frontend/AppSettings/services/ToastService';
import AnchoredPopover from './AnchoredPopover';
import { DateLock } from './TimesheetDayLockService';
import { WEEKDAY_NAMES, describeDateLock, formatMonthDay, serverErrorMessage } from './dayLockHelpers';
import './TimesheetDayLocks.css';

interface DayLockHeaderToggleProps {
  /** Sunday = 0 … Saturday = 6. */
  weekday: number;
  /** The WEEKDAY is locked. */
  locked: boolean;
  /** True until the current locks have loaded. */
  disabled?: boolean;
  /** Saves the new lock state for this weekday; rejects with the server's error. */
  onChange: (lock: boolean) => Promise<{ cancelledRequests: number }>;
  /**
   * This column's date (`yyyy-MM-dd`). With it (and the handler below) the popover also offers
   * "Unlock <this date>" / "Unlock <month day> every year" when such a date lock exists
   * (plans/todo/timesheet-date-locks.md). New date locks, one-off or every year, are made in the
   * Locked days drawer only (plans/todo/timesheet-lock-days-from-drawer.md).
   */
  dateKey?: string;
  /** The one-off and annual date locks already on this date. */
  dateLocks?: { oneOff?: DateLock; annual?: DateLock };
  onUnlockDate?: (lock: DateLock) => Promise<{ cancelledRequests: number }>;
  /**
   * `'icon'` (default): the small 🔒 / open lock. `'chip'` (the firm-wide grid only,
   * plans/todo/timesheet-refined-grid.md F5): a locked day reads as an outlined "Day locked" chip
   * and the unlocked icon stays fully visible. Same button, label, title and popover either way.
   */
  variant?: 'icon' | 'chip';
}

type Action = 'weekday' | 'date' | 'annual';

function cancelledNote(count: number): string {
  return count > 0 ? ` ${count} pending request${count === 1 ? ' was' : 's were'} cancelled.` : '';
}

/**
 * Admin-only 🔒 on a day header (plan F1). Two clicks: the 🔒, then "Lock Saturdays" (every
 * Saturday). Date locks are added from the Locked days drawer, not here (owner: every-year
 * 2026-10-21, this-date-only 2026-10-26). A date lock already on this column can still be undone
 * here — "Unlock Thu, Nov 5, 2026" / "Unlock Nov 5 every year" — so a hatched header never shows a
 * lock it cannot undo; unlocking cancels the pending requests no other lock still covers
 * (decision f), which the popover says before the admin confirms. The weekday action and the date
 * actions then sit under separate headings so "every Saturday" never reads like "this date" (HD6).
 */
const DayLockHeaderToggle: React.FC<DayLockHeaderToggleProps> = ({
  weekday,
  locked,
  disabled = false,
  onChange,
  dateKey,
  dateLocks,
  onUnlockDate,
  variant = 'icon',
}) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<Action | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const plural = `${WEEKDAY_NAMES[weekday]}s`;
  const close = useCallback(() => setOpen(false), []);
  const oneOff = dateLocks?.oneOff;
  const annual = dateLocks?.annual;
  const withDates = Boolean(dateKey && onUnlockDate && (oneOff || annual));
  const anyLock = locked || Boolean(oneOff) || Boolean(annual);
  const dateText = dateKey ? describeDateLock({ date: dateKey, month: null, day: null }) : '';
  const monthDay = dateKey ? formatMonthDay(dateKey) : '';

  const run = async (action: Action, work: () => Promise<string>, failure: string) => {
    setBusy(action);
    try {
      toastService.success(await work());
      setOpen(false);
    } catch (err) {
      toastService.error(serverErrorMessage(err) || failure);
    } finally {
      setBusy(null);
    }
  };

  const confirmWeekday = () =>
    run(
      'weekday',
      async () => {
        const { cancelledRequests } = await onChange(!locked);
        return locked ? `${plural} unlocked.${cancelledNote(cancelledRequests)}` : `${plural} locked.`;
      },
      `Could not ${locked ? 'unlock' : 'lock'} ${plural}.`,
    );

  const unlockDate = (action: 'date' | 'annual', existing: DateLock) => {
    const what = action === 'date' ? dateText : `${monthDay} every year`;
    return run(
      action,
      async () => {
        const { cancelledRequests } = await onUnlockDate!(existing);
        return `${what} unlocked.${cancelledNote(cancelledRequests)}`;
      },
      `Could not unlock ${what}.`,
    );
  };

  const title = anyLock
    ? [locked && `${plural} are locked`, oneOff && `${dateText} is locked (${oneOff.label})`, annual && `${monthDay} is locked every year (${annual.label})`]
        .filter(Boolean)
        .join('; ') + ' — click to change'
    : `Lock ${plural}`;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`tdl-header-toggle ${variant === 'chip' ? 'tdl-header-toggle--chip ' : ''}${anyLock ? 'is-locked' : ''}`}
        aria-label={`Day lock for ${plural}`}
        aria-pressed={anyLock}
        aria-expanded={open}
        disabled={disabled}
        title={title}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        <FontAwesomeIcon icon={anyLock ? faLock : faLockOpen} />
        {variant === 'chip' && anyLock && <span className="tdl-header-chip-text">Day locked</span>}
      </button>
      <AnchoredPopover
        anchor={buttonRef.current}
        open={open}
        onClose={close}
        width={withDates ? 300 : 260}
        ariaLabel={`${locked ? 'Unlock' : 'Lock'} ${plural}`}
      >
        {withDates && <p className="tdl-popover-heading">Every {WEEKDAY_NAMES[weekday]}</p>}
        <p className="tdl-popover-text">
          {locked
            ? `Unlock ${plural}? Everyone can enter ${WEEKDAY_NAMES[weekday]} time directly again, and pending ${WEEKDAY_NAMES[weekday]} requests are cancelled unless another lock still covers them.`
            : `Lock ${plural}? Employees will request ${WEEKDAY_NAMES[weekday]} time instead of entering it. Administrators are not affected.`}
        </p>
        <div className="tdl-popover-actions">
          <button type="button" className="tdl-btn tdl-btn-primary" onClick={confirmWeekday} disabled={busy !== null}>
            {busy === 'weekday' ? 'Saving…' : `${locked ? 'Unlock' : 'Lock'} ${plural}`}
          </button>
          <button type="button" className="tdl-btn" onClick={close} disabled={busy !== null}>
            Cancel
          </button>
        </div>
        {withDates && (
          <div className="tdl-popover-section">
            <p className="tdl-popover-heading">This date</p>
            <p className="tdl-popover-text">
              Unlocking cancels pending requests on{' '}
              {oneOff && annual ? `this date (or on ${monthDay} in every year)` : oneOff ? 'this date' : `${monthDay} in every year`}{' '}
              unless another lock still covers them.
            </p>
            <div className="tdl-popover-actions tdl-popover-actions-stacked">
              {oneOff && (
                <button type="button" className="tdl-btn" onClick={() => unlockDate('date', oneOff)} disabled={busy !== null}>
                  {busy === 'date' ? 'Saving…' : `Unlock ${dateText}`}
                </button>
              )}
              {annual && (
                <button type="button" className="tdl-btn" onClick={() => unlockDate('annual', annual)} disabled={busy !== null}>
                  {busy === 'annual' ? 'Saving…' : `Unlock ${monthDay} every year`}
                </button>
              )}
            </div>
          </div>
        )}
      </AnchoredPopover>
    </>
  );
};

export default DayLockHeaderToggle;
